import isEqual from "lodash/isEqual"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import find from "lodash/find"
import each from "lodash/each"
import { createRef, Component } from "react"
import PropTypes from "prop-types"
import { getWindow } from "utils-lib/getWindow"
import getContentUrl from "utils-lib/getContentUrl"
import ContentLoader from "react-content-loader"
import getMapMarkerCoordinates from "./utils/getMapMarkerCoordinates"
import googleMapStyles from "./styles/index.json"
import "./MapComponent.scss"

const ssrWindow = getWindow()

const URL_IMG_PIN_USER = getContentUrl(
  `/content/dam/wm/icons/location-pin-user-dot-icon.svg`,
)

// Lazily resolved inside updateMapCenter — google.maps.LatLng may not be
// available at module-evaluation time on a Windows cold load.
const US_CENTER = { lat: 37.0902, lng: -95.7129 }
const CANADA_CENTER = { lat: 57.7022766, lng: -113.6884396 }

const getLatLng = (coords) => {
  if (typeof ssrWindow.google?.maps?.LatLng === `function`) {
    return new ssrWindow.google.maps.LatLng(coords.lat, coords.lng)
  }
  // Fallback plain object — google.maps.Map.setCenter accepts {lat, lng} too
  return coords
}

export const MapComponentSkeleton = () => (
  <ContentLoader className="map-skeleton">
    <rect x="0" y="0" rx="5" ry="5" width="100%" height="100%" />
  </ContentLoader>
)

class MapComponent extends Component {
  constructor(props) {
    super(props)
    this.map = null
    this.mapMarkers = []
    this.userLocationMapMarker = null
    this.hoveredMapMarker = null
    this.mapRef = createRef()
    this._mapsReadyInterval = null
  }

  componentDidMount() {
    // The Google Maps script loads asynchronously. On slower machines or cold
    // loads (e.g. Windows Edge without cache) the API may not be available yet
    // when this lifecycle method runs. Poll until it is ready.
    this.initMapWhenReady()
    ssrWindow.addEventListener(`resize`, this.handleResize)
  }

  initMapWhenReady() {
    // google.maps namespace can exist before google.maps.Map is constructible
    // on a cold Windows load — check for the Map constructor specifically.
    if (typeof ssrWindow.google?.maps?.Map === `function`) {
      this.initMap()
    } else {
      this._mapsReadyInterval = setInterval(() => {
        if (typeof ssrWindow.google?.maps?.Map === `function`) {
          clearInterval(this._mapsReadyInterval)
          this._mapsReadyInterval = null
          this.initMapWhenReady()
        }
      }, 100)
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return nextProps !== this.props || nextState !== this.state
  }

  isMapReady() {
    return (
      this.map !== null && typeof ssrWindow.google?.maps?.LatLng === `function`
    )
  }

  componentDidUpdate(prevProps) {
    // Map is initialised asynchronously (idle event). Bail out early if it
    // isn't ready yet — all the methods below require a live map instance.
    if (!this.isMapReady()) return

    // only if the location or the user choice changed, update the markers
    // don't update the entire map if only a map marker was clicked
    const { hoveredMapMarkerId, hasSuggestion, selectedMapMarkerId } =
      this.props
    const locCoordinatesOld =
      prevProps.userLocationCoordinates &&
      JSON.stringify(prevProps.userLocationCoordinates)
    const locCoordinatesNew =
      this.props.userLocationCoordinates &&
      JSON.stringify(this.props.userLocationCoordinates)
    const isOldLocation =
      locCoordinatesNew &&
      locCoordinatesOld &&
      locCoordinatesNew === locCoordinatesOld
    const hasNewLocations =
      prevProps.mapMarkers &&
      !isEqual(this.props.mapMarkers, prevProps.mapMarkers)
    const needToRender =
      prevProps.selectedMapMarkerId && !this.props.selectedMapMarkerId

    if (prevProps.hoveredMapMarkerId && !hoveredMapMarkerId) {
      this.removeHoveredMapMarker()
      if (needToRender) {
        this.addUserLocationMarker()
        this.removeMapMarkers()
        this.addMapMarkers()
      }
    } else if (
      !prevProps.hoveredMapMarkerId &&
      hoveredMapMarkerId &&
      hoveredMapMarkerId !== selectedMapMarkerId
    ) {
      this.addHoveredMapMarker()
    } else if (
      hoveredMapMarkerId &&
      hoveredMapMarkerId !== selectedMapMarkerId &&
      prevProps.hoveredMapMarkerId !== hoveredMapMarkerId
    ) {
      this.removeHoveredMapMarker()
      this.addHoveredMapMarker()
    } else if (hoveredMapMarkerId === null && hasSuggestion) {
      this.updateMapCenter()
      this.offsetMapCenter()
      this.updateMapZoom()
      if (!isOldLocation || hasNewLocations) {
        this.addUserLocationMarker()
        this.removeMapMarkers()
        this.addMapMarkers()
      }
      this.removeHoveredMapMarker()
      if (
        !isEmpty(get(this.props.errorMessage, `props`)) &&
        this.props.errorMessage !== `Loading...`
      ) {
        this.resetMapCenter()
        this.offsetMapCenter()
        this.removeUserLocationMapMarker()
        this.removeMapMarkers()
        ssrWindow.addEventListener(`resize`, this.handleResize)
      }
    } else if (!hasSuggestion) {
      this.resetMapCenter()
      this.offsetMapCenter()
      this.removeUserLocationMapMarker()
      this.removeMapMarkers()
      this.removeHoveredMapMarker()
      ssrWindow.addEventListener(`resize`, this.handleResize)
    }
  }

  componentWillUnmount() {
    if (this._mapsReadyInterval) {
      clearInterval(this._mapsReadyInterval)
      this._mapsReadyInterval = null
    }
    ssrWindow.removeEventListener(`resize`, this.handleResize)
  }

  handleResize = () => {
    if (!this.isMapReady()) return
    this.updateMapCenter()
    this.offsetMapCenter()
  }

  offsetMapCenter = () => {
    if (!this.map) return
    const offsetPercentage = 0.15
    const offsetPixels = ssrWindow.innerWidth * offsetPercentage
    if (ssrWindow.innerWidth < 1024) {
      this.map.panBy(-1 * offsetPixels, 0)
    }
  }

  resetMapCenter = () => {
    if (!this.map) return
    const { defaultMapCenter, defaultMapZoom } = this.props
    if (
      defaultMapCenter &&
      typeof defaultMapCenter.lat === "number" &&
      typeof defaultMapCenter.lng === "number"
    ) {
      this.map.setCenter(defaultMapCenter)
    }
    this.map.setZoom(defaultMapZoom)
  }

  updateMapCenter() {
    if (!this.map) return
    const {
      userLocationCoordinates,
      defaultMapCenter,
      mapMarkers,
      selectedMapMarkerId,
      siteLanguage = {},
    } = this.props

    // If a location has been selected, center map on that location.
    const countryCenter = getLatLng(
      get(siteLanguage, `language`, `en_US`) === `en_US`
        ? US_CENTER
        : CANADA_CENTER,
    )
    let newCenterCoordinates = !isEmpty(this.props.defaultMapCenter)
      ? defaultMapCenter
      : countryCenter

    if (selectedMapMarkerId !== null) {
      const selectedMapMarkerCoordinates = getMapMarkerCoordinates(
        mapMarkers,
        selectedMapMarkerId,
      )

      if (
        selectedMapMarkerCoordinates !== null &&
        selectedMapMarkerCoordinates.lat !== undefined &&
        selectedMapMarkerCoordinates.lng !== undefined
      ) {
        newCenterCoordinates = getLatLng(selectedMapMarkerCoordinates)
      }

      // Otherwise center map on the user location.
    } else if (
      userLocationCoordinates !== null &&
      userLocationCoordinates.lat !== undefined &&
      userLocationCoordinates.lng !== undefined
    ) {
      // if user has selected an address center the map at their address
      newCenterCoordinates = getLatLng(userLocationCoordinates)
    }
    this.map.setCenter(newCenterCoordinates)
  }

  updateMapZoom() {
    if (!this.map) return
    const {
      mapMarkers,
      defaultMapZoom,
      defaultShowOneMapZoom,
      defaultShowAllMapZoom,
      selectedMapMarkerId,
      hoveredMapMarkerId,
    } = this.props

    let newMapZoom = defaultMapZoom
    // Do not zoom in after clicking on a map marker.
    if (selectedMapMarkerId !== null && hoveredMapMarkerId === null) {
      newMapZoom = defaultShowOneMapZoom
    } else if (mapMarkers.length > 0) {
      newMapZoom = defaultShowAllMapZoom
    }
    this.map.setZoom(newMapZoom)
  }

  addMapMarker(item) {
    const { hoveredMapMarkerId, onMapMarkerClick, onMouseEnterItem } =
      this.props

    const marker = new ssrWindow.google.maps.Marker({
      position: item.position,
      map: this.map,
      title: item.title,
    })
    marker.setIcon(item.icon)
    // clear previously selected map marker
    if (item.uniqueId === hoveredMapMarkerId) {
      this.removeHoveredMapMarker()
      // save new selected map marker
      this.hoveredMapMarker = marker
    } else {
      marker.addListener(`click`, () => {
        onMapMarkerClick(item.uniqueId)
      })
      marker.addListener(`mouseover`, () => {
        onMouseEnterItem(item.uniqueId)
      })
      marker.addListener(`mouseout`, () => {})
      this.mapMarkers.push(marker)
    }
    return null
  }

  addMapMarkers() {
    this.props.mapMarkers.map((item) => {
      this.addMapMarker(item)
      return null
    })
  }

  addUserLocationMarker() {
    const { userLocationCoordinates } = this.props

    this.removeUserLocationMapMarker()

    if (
      userLocationCoordinates !== null &&
      userLocationCoordinates.lat !== undefined &&
      userLocationCoordinates.lng !== undefined
    ) {
      this.userLocationMapMarker = new ssrWindow.google.maps.Marker({
        position: getLatLng({
          lat: userLocationCoordinates.lat,
          lng: userLocationCoordinates.lng,
        }),
        map: this.map,
        icon: URL_IMG_PIN_USER,
        title: `Start`,
      })
    }
  }

  addHoveredMapMarker() {
    const {
      mapMarkers,
      hoveredMapMarkerId,
      onMapMarkerClick,
      onMouseLeaveItem,
    } = this.props

    const mapMarker = find(mapMarkers, [`uniqueId`, hoveredMapMarkerId])

    if (mapMarker !== undefined) {
      const marker = new ssrWindow.google.maps.Marker({
        position: mapMarker.position,
        map: this.map,
        title: mapMarker.title,
        icon: mapMarker.icon,
      })

      marker.addListener(`click`, () => {
        onMapMarkerClick(hoveredMapMarkerId)
      })
      marker.addListener(`mouseout`, () => {
        onMouseLeaveItem()
      })

      this.hoveredMapMarker = marker
    }
  }

  removeMapMarkers() {
    each(this.mapMarkers, (marker) => marker.setMap(null))
    this.mapMarkers = []
  }

  removeHoveredMapMarker() {
    if (this.hoveredMapMarker !== null) {
      this.hoveredMapMarker.setMap(null)
      this.hoveredMapMarker = null
    }
  }

  removeUserLocationMapMarker() {
    if (this.userLocationMapMarker !== null) {
      this.userLocationMapMarker.setMap(null)
      this.userLocationMapMarker = null
    }
  }

  initMap() {
    const mapOptions = {
      disableDefaultUI: false,
      mapTypeControl: false,
      streetViewControl: false,
      scrollwheel: false,
      fullscreenControl: false,
      styles: googleMapStyles,
    }
    this.map = new ssrWindow.google.maps.Map(this.mapRef.current, mapOptions)

    // Wait for the map to be fully rendered before setting center/zoom/markers.
    // This prevents failures on Windows where the Map object exists but tiles
    // are not yet ready on the first cold load.
    ssrWindow.google.maps.event.addListenerOnce(this.map, `idle`, () => {
      this.updateMapCenter()
      this.updateMapZoom()
      this.addMapMarkers()
      this.addUserLocationMarker()
    })
  }

  render() {
    return (
      <div
        data-testid="MapComponent"
        className={`MapComponent ${
          this.props.hasMapLocations ? `has-loc` : ``
        }`}
      >
        <div className="d-inline-block w-100">
          <div
            className="google-map w-auto"
            ref={this.mapRef}
            style={{ width: `auto` }}
          />
        </div>
      </div>
    )
  }
}

MapComponent.propTypes = {
  defaultMapCenter: PropTypes.object,
  defaultMapZoom: PropTypes.number,
  defaultShowOneMapZoom: PropTypes.number,
  defaultShowAllMapZoom: PropTypes.number,
  hoveredMapMarkerId: PropTypes.string,
  mapMarkers: PropTypes.array.isRequired,
  onMapMarkerClick: PropTypes.func.isRequired,
  onMouseEnterItem: PropTypes.func.isRequired,
  onMouseLeaveItem: PropTypes.func.isRequired,
  selectedMapMarkerId: PropTypes.string,
  userLocationCoordinates: PropTypes.object,
  errorMessage: PropTypes.any,
  hasSuggestion: PropTypes.bool,
  siteLanguage: PropTypes.object,
  hasMapLocations: PropTypes.bool.isRequired,
}

MapComponent.defaultProps = {
  // US :{lat: 37.0902, lng: -95.7129}
  defaultMapCenter: {},
  defaultMapZoom: 8,
  defaultShowOneMapZoom: 14, // 17
  defaultShowAllMapZoom: 9,
  hoveredMapMarkerId: null,
  selectedMapMarkerId: null,
  userLocationCoordinates: null,
  errorMessage: null,
  hasSuggestion: false,
  siteLanguage: {},
}

export default MapComponent
